import type { PimsWriteMode } from "@clinic/clinicClient";
import {
  useClinic,
  useUpdateFlushInterval,
  useUpdatePimsWriteMode,
} from "@clinic/clinicQueries";
import {
  ConfirmDialog,
  Select,
  SettingsCard,
} from "@scribenote/brian-design-system";
import { useState } from "react";

// The three modes: Automatic (`direct` - Brian writes via Hans each turn), Batch
// (`batch` - Brian queues writes and flushes them to Hans on a schedule), and
// Manual (`manual` - staff action each write as a check-off card).
const MODE_OPTIONS: { label: string; value: PimsWriteMode }[] = [
  { label: "Automatic", value: "direct" },
  { label: "Batch", value: "batch" },
  { label: "Manual", value: "manual" },
];

/** The two modes where Brian writes on its own, without a person checking each
 * write first. They are the ones that wear the shimmer. */
const isAutonomous = (mode: PimsWriteMode | null): boolean =>
  mode === "direct" || mode === "batch";

/**
 * The autonomous modes keep their shimmering purple label (the animation is
 * owned by index.css): it is the signal that Brian writes without being asked,
 * and it belongs on the mode's own name. The child selector puts it on the
 * select trigger's label span, which is the only text in the box.
 */
const SHIMMERING_LABEL =
  "[&>span]:animate-automatic-shimmer [&>span]:font-semibold";

// How often batch mode flushes queued writes to Hans. Presets keep it bounded
// (the BE accepts 1-1440); the default cadence is 10 minutes. Values are the
// minute counts as strings, which is the vocabulary the select speaks.
const INTERVAL_OPTIONS: { label: string; value: string }[] = [
  { label: "5 minutes", value: "5" },
  { label: "10 minutes", value: "10" },
  { label: "15 minutes", value: "15" },
  { label: "30 minutes", value: "30" },
  { label: "60 minutes", value: "60" },
];
const DEFAULT_INTERVAL = 10;

/**
 * Clinic-level control for `pims_write_mode` (BE-1832 Part B). Reads the current
 * mode off `useClinic` and lets staff switch between Automatic, Batch, and
 * Manual. Every switch is gated behind a confirm dialog that spells out what the
 * mode does before it takes effect; Batch also exposes a flush-interval picker.
 */
export const ClinicSettingsPanel = () => {
  const { data: clinic } = useClinic();
  const updateMode = useUpdatePimsWriteMode();
  const updateInterval = useUpdateFlushInterval();
  // The mode a switch is waiting to apply, held while the confirm dialog is up.
  const [pending, setPending] = useState<PimsWriteMode | null>(null);

  const current = clinic?.pimsWriteMode ?? null;
  const interval = clinic?.flushIntervalMinutes ?? DEFAULT_INTERVAL;

  const handleIntervalChange = (value: string) => {
    const minutes = Number(value);
    if (minutes === interval) return;
    updateInterval.mutate(minutes);
  };

  const apply = (mode: PimsWriteMode) => {
    updateMode.mutate(mode, { onSettled: () => setPending(null) });
  };

  const handleChange = (value: string) => {
    const mode = value as PimsWriteMode; // "direct" | "batch" | "manual"
    if (mode === current) return;
    // Both switches confirm first - the dialog explains what the mode does.
    setPending(mode);
  };

  return (
    <div className="flex h-full flex-col gap-4 pr-2 font-body">
      <SettingsCard
        title="PIMS write mode"
        subtitle="How I handle writes to your practice management system."
        data-testid="clinic-settings-card"
      >
        <Select
          data-testid="clinic-pims-write-mode"
          options={MODE_OPTIONS}
          value={current}
          onValueChange={handleChange}
          placeholder="Select a mode"
          disabled={!clinic || updateMode.isPending}
          className={isAutonomous(current) ? SHIMMERING_LABEL : undefined}
        />

        {current === "batch" && (
          <div className="mt-4">
            <Select
              data-testid="clinic-flush-interval"
              label="Flush queued writes every"
              options={INTERVAL_OPTIONS}
              value={String(interval)}
              onValueChange={handleIntervalChange}
              placeholder="Select an interval"
              disabled={!clinic || updateInterval.isPending}
            />
          </div>
        )}
      </SettingsCard>

      <SettingsCard
        title="Agent transcript"
        subtitle="My current agent transcript epoch for this clinic. Read-only, and useful for diagnostics and support. It’s empty until I first run, and bumps when the context is rotated."
        data-testid="agent-transcript-card"
      >
        <dl className="flex flex-col gap-3 text-sm">
          <div className="flex flex-col gap-0.5">
            <dt className="text-annotation-caps text-annotation">
              Transcript epoch
            </dt>
            <dd className="font-mono break-all text-ink">
              {clinic?.agentTranscriptEpoch != null ? (
                clinic.agentTranscriptEpoch
              ) : (
                <span className="font-body text-annotation">
                  No active session
                </span>
              )}
            </dd>
          </div>
        </dl>
      </SettingsCard>

      <ConfirmDialog
        open={pending !== null}
        // Conditional: the dialog stays mounted while closed, so an
        // unconditional handle would report an open dialog that isn't there.
        data-testid={
          pending !== null ? "clinic-pims-write-mode-confirm" : undefined
        }
        title={
          pending === "direct"
            ? "Let Brian write to the PIMS?"
            : pending === "batch"
              ? "Batch writes to the PIMS?"
              : "Switch to manual writes?"
        }
        confirmLabel={
          pending === "direct"
            ? "Enable automatic writes"
            : pending === "batch"
              ? "Enable batch writes"
              : "Switch to manual"
        }
        cancelLabel="Cancel"
        busy={updateMode.isPending}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (pending) apply(pending);
        }}
        confirmTestId="clinic-pims-write-mode-confirm-confirm"
        cancelTestId="clinic-pims-write-mode-confirm-cancel"
      >
        <p className="text-sm text-ink-body">
          {pending === "direct" ? (
            <>
              Switching to <strong className="font-medium">Automatic</strong>{" "}
              means I write to your PIMS myself, and staff won’t verify each
              write first. Continue?
            </>
          ) : pending === "batch" ? (
            <>
              Switching to <strong className="font-medium">Batch</strong> means
              I queue up PIMS writes and send them together on a schedule. Still
              automatic: staff won’t verify each write first. Continue?
            </>
          ) : (
            <>
              Switching to <strong className="font-medium">Manual</strong> means
              I won’t write to the PIMS. Each write becomes a task for staff to
              do and check off. Continue?
            </>
          )}
        </p>
      </ConfirmDialog>
    </div>
  );
};
